import React, { useState, useEffect } from 'react';
import { inject, observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';

import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { Theme } from '@material-ui/core/styles/createTheme';
import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextField from '@material-ui/core/TextField';
import MenuItem from '@material-ui/core/MenuItem';

import Background from 'components/Layout/Background';
import PieGraph from 'components/Graph/PieGraph';
import PortfolioTableCurrent from 'components/PortfolioTables/PortfolioTableCurrent';
import DevelopmentLineGraph from 'components/Graph/DevelopmentLineGraph';
import DevelopmentTab from './components/DevelopmentTab';
import CheckIcon from 'components/Icons/CheckIcon';
import CancelIcon from 'components/Icons/CancelIcon';
import Info from 'components/Tooltip/Info';

import { RootStore } from 'stores/rootStore';
import { DevelopmentStore, BucketTypes } from 'stores/account/developmentStore';
import useStyles, { useDevelopmentStyles } from 'components/Bucket/styles'; //
import { AccountDetailData } from 'stores/account/model';
import { BucketType } from 'stores/workflowStores/newWorkflowPlanStore/model/interfaces';
import DevelopmentOverview from './components/DevelopmentOverview';
import { fractionToPercent } from 'helpers/helpers';

interface Props {
  rootStore?: RootStore;
  accountId: string;
  detail: AccountDetailData | null;
  userMode: boolean;
}

export const periods = [
  { value: 'ALL_TIME', label: 'accountDetail_graph_allTime' },
  { value: 'THIS_YEAR', label: 'accountDetail_graph_thisYear' },
  { value: 'LAST_3M', label: 'accountDetail_graph_3months' },
  { value: 'LAST_6M', label: 'accountDetail_graph_6months' },
  { value: 'LAST_YEAR', label: 'accountDetail_graph_year' },
];

const Development: React.FC<Props> = ({ rootStore, accountId, detail, userMode }) => {
  const { t } = useTranslation();
  const classes = useStyles({ simple: true });
  const devClasses = useDevelopmentStyles();
  const [store] = useState(new DevelopmentStore(rootStore!, accountId));

  useEffect(() => {
    (async () => {
      await store.fetchData();
      await store.fetchGraphData(true);
      store.setActiveBucket(0, BucketType.TOTAL);
    })();
  }, [store]);

  const activeBucket = store.activeBuckets[store.activeBucket - 1];
  const showDrift = activeBucket && activeBucket.drift !== undefined && activeBucket.balance.value > 0;
  const mobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('xs'), { noSsr: true });

  return (
    <>
      {store.graphData && store.data && detail && store.dataReady && (
        <Grid container spacing={0}>
          <Grid container item xs={12} spacing={0} className={classes.wrapper}>
            {!mobile && (
              <>
                {store.bucketTabs.map(
                  (
                    bucket,
                    i, // active buckets
                  ) => (
                    <Grid
                      item
                      xs={3}
                      key={bucket.type}
                      onClick={() => {
                        store.setActiveBucket(i, bucket.type);
                      }}
                      className={`${classes.tabWrapper} ${i === store.activeBucket ? classes.active : ''}`}
                    >
                      <DevelopmentTab
                        {...bucket}
                        currency={detail.currency}
                        activated={i === store.activeBucket}
                        portfolioOnly={store.data?.portfolioOnly}
                      />
                      <div className="_activeCurtain" />
                    </Grid>
                  ),
                )}
              </>
            )}
          </Grid>

          <Background className={classes.tabsSection}>
            <Grid container item xs={12} spacing={0} alignItems="center" className={devClasses.mainWrapper}>
              {mobile && (
                <>
                  {store.bucketTabs.length > 1 && (
                    <TextField
                      select={true}
                      defaultValue={store.activeBucket}
                      onChange={(e) => store.setActiveBucket(Number(e.target.value))}
                      className={`${classes.bucketsSelect} ${store.activeBucketType}`}
                    >
                      {store.bucketTabs.map((bucket, i) => (
                        <MenuItem key={bucket.type} value={i}>
                          {t(bucket.title)}
                        </MenuItem>
                      ))}
                    </TextField>
                  )}
                </>
              )}

              <DevelopmentOverview store={store} detail={detail} periods={periods} />

              <DevelopmentLineGraph
                data={store.graphData!}
                currency={detail.currency}
                portfolio={store.activeBucketType}
                store={store}
              />
            </Grid>

            {store.activeBucketType !== BucketTypes.TOTAL && (
              <>
                <Typography variant="h2" className={devClasses.detailTitle}>
                  {t('accountDetail_graph_portfolioDetailTitle')}
                </Typography>
                <Grid container item xs={12} spacing={0}>
                  <Grid container item xs={12} spacing={0} alignItems="stretch">
                    {store.pie.data ? (
                      <>
                        <div className={classes.pieWrapper}>
                          <PieGraph
                            data={store.pie}
                            settings={{
                              chart: {
                                spacingTop: 80,
                              },
                            }}
                          />
                        </div>

                        <Grid
                          container
                          item
                          xs={12}
                          spacing={0}
                          justifyContent="center"
                          alignItems="center"
                          className={devClasses.checks}
                        >
                          <Grid item>
                            {activeBucket.automaticRebalancing ? (
                              <CheckIcon htmlColor={'#818284'} />
                            ) : (
                              <CancelIcon htmlColor={'#bdbdbd'} />
                            )}
                            <span
                              style={{
                                color: activeBucket.automaticRebalancing ? '#818284' : 'rgb(160,160,160)',
                              }}
                            >
                              {t('accountDetail_plan_automaticRebalancing')}
                            </span>
                          </Grid>
                        </Grid>

                        <Grid
                          container
                          item
                          xs={12}
                          spacing={0}
                          justifyContent="center"
                          alignItems="center"
                          className={devClasses.checks}
                        >
                          <Grid item alignItems="center">
                            {activeBucket.currencyHedging ? (
                              <CheckIcon htmlColor={'#818284'} />
                            ) : (
                              <CancelIcon htmlColor={'#bdbdbd'} />
                            )}
                            <span
                              style={{
                                color: activeBucket.currencyHedging ? '#818284' : 'rgb(160,160,160)',
                              }}
                            >
                              {t('accountDetail_plan_currencyHedging')}
                            </span>
                          </Grid>
                        </Grid>
                      </>
                    ) : null}
                  </Grid>

                  {showDrift && (
                    <Grid container item xs={12} spacing={0} className={devClasses.bucketMisc}>
                      <Grid item xs={12} sm={6} style={{ textAlign: 'left' }}>
                        <span style={{ fontSize: '18px', fontWeight: 800 }}>{t('bucket_drift')}</span>
                        <Info
                          content={
                            <span
                              dangerouslySetInnerHTML={{
                                __html: t(`bucket_drift_tooltip`, {
                                  interpolation: { escapeValue: false },
                                }),
                              }}
                            />
                          }
                          heading
                        />
                        <span className={devClasses.drift}>
                          <span
                            className={
                              activeBucket!.drift! > activeBucket!.options.driftThreshold[1]
                                ? 'HIGH'
                                : activeBucket!.drift! > activeBucket!.options.driftThreshold[0]
                                ? 'MID'
                                : 'LOW'
                            }
                          >
                            {fractionToPercent(activeBucket?.drift!)}
                          </span>
                        </span>
                      </Grid>
                    </Grid>
                  )}

                  <Grid item xs={12} style={{ marginTop: '8px' }}>
                    <PortfolioTableCurrent
                      detailMode={true}
                      userMode={userMode}
                      historyPage={true}
                      assetCategories={activeBucket?.portfolio?.assetCategories}
                      terAverage={activeBucket?.portfolio?.terAverage}
                      distributionTotal={activeBucket?.portfolio?.distributionTotal}
                      savingIsins={store.data.options.specialAssets.savings}
                      drift={activeBucket?.balance.value !== 0 ? activeBucket?.instrumentDrift : undefined}
                    />
                  </Grid>
                </Grid>
              </>
            )}
          </Background>
        </Grid>
      )}
    </>
  );
};

export default React.memo(inject('rootStore')(observer(Development)));
